import './Button.css';
import type { JSX, Ref } from 'preact';
import { Icon } from './Icon';
import type { IconName } from './icons';

export type ButtonVariant = 'default' | 'ghost' | 'inverted';

type NativeAttrs = Omit<
  JSX.HTMLAttributes<HTMLButtonElement>,
  'class' | 'className' | 'children' | 'type' | 'disabled' | 'onClick' | 'ref' | 'aria-pressed' | 'icon' | 'label'
>;

export interface ButtonProps extends NativeAttrs {
  /** Rótulo visível; é o nome acessível (§3.8). Texto vem do dono, sempre por t(). */
  label: string;
  /** Ícone opcional à esquerda, 14 px. */
  icon?: IconName;
  variant?: ButtonVariant;
  disabled?: boolean;
  /** Estado ligado (`aria-pressed`); omitido = botão sem estado. A aparência de ligado é só da variante `default`. */
  pressed?: boolean;
  onClick?: (event: MouseEvent) => void;
  /** Navega para outra página: vira `<a href>` com a mesma aparência (§3.8, Acessibilidade). */
  href?: string;
  /**
   * O dono esconde `.button__label` por container query (vira só ícone, ex.: TvButton ≤ 1520, §4.10); o rótulo
   * passa a ser também o `aria-label`, para o nome acessível sobreviver.
   */
  collapsibleLabel?: boolean;
  /** Classes extras do dono (ex.: `tv-hide`). */
  class?: string;
  buttonRef?: Ref<HTMLButtonElement>;
}

/** Botão de texto (§3.8): variantes `default`, `ghost` e `inverted`; `<button type="button">` nativo. */
export function Button({
  label,
  icon,
  variant = 'default',
  disabled = false,
  pressed,
  onClick,
  href,
  collapsibleLabel = false,
  class: extra,
  buttonRef,
  ...rest
}: ButtonProps) {
  const cls = `button button--${variant}${extra ? ` ${extra}` : ''}`;
  const content = (
    <>
      {icon && <Icon name={icon} size={14} />}
      <span class="button__label">{label}</span>
    </>
  );
  if (href !== undefined) {
    return (
      <a
        {...(rest as JSX.HTMLAttributes<HTMLAnchorElement>)}
        class={cls}
        data-testid="button"
        href={href}
        aria-label={collapsibleLabel ? label : undefined}
      >
        {content}
      </a>
    );
  }
  return (
    <button
      {...rest}
      ref={buttonRef}
      type="button"
      class={cls}
      data-testid="button"
      disabled={disabled}
      aria-pressed={pressed === undefined ? undefined : pressed ? 'true' : 'false'}
      aria-label={collapsibleLabel ? label : undefined}
      onClick={disabled ? undefined : onClick}
    >
      {content}
    </button>
  );
}
